import { Assistant } from "@inrev/assistant";
import type { AssistantConversationId } from "@inrev/common";
import { memo, useMemo } from "react";
import { match } from "ts-pattern";
import { AdminAssistantChartNotice } from "./AdminAssistantChartNotice";
import { AdminAssistantFredChart } from "./fred/AdminAssistantFredChart";
import { AdminAssistantImageChart } from "./image/AdminAssistantImageChart";
import { AdminAssistantOrgChart } from "./orgchart/AdminAssistantOrgChart";
import { AdminAssistantSfaaChart } from "./sfaa/AdminAssistantSfaaChart";

/**
 * Draws a ```chart block from an assistant answer. A FRED or SFAA block names what to plot, and
 * the data comes from the same endpoints as Market Analytics; an image block names a PNG the
 * agent drew; an org chart block names the structure a tool stored.
 *
 * Memoized: the markdown around it re-renders on every streamed token, and the block's props only
 * change while its own fence streams.
 */
export const AdminAssistantChart = memo(
	({
		source,
		open,
		streaming,
		conversationId,
	}: {
		source: string;
		/** The block's closing fence has not arrived, so incomplete JSON is not yet an error. */
		open: boolean;
		/** The whole answer is still streaming; a drawn chart's image is stored only once it ends. */
		streaming: boolean;
		/** Where a drawn chart's image is stored; undefined outside a saved conversation. */
		conversationId: AssistantConversationId | undefined;
	}) => {
		const chart = useMemo(() => Assistant.Charts.parseChart(source), [source]);

		if (chart === undefined) {
			return open ? (
				<AdminAssistantChartNotice>Drawing chart…</AdminAssistantChartNotice>
			) : (
				<AdminAssistantChartNotice danger>This chart could not be drawn.</AdminAssistantChartNotice>
			);
		}

		return match(chart)
			.with({ source: "fred" }, (fred) => <AdminAssistantFredChart chart={fred} />)
			.with({ source: "sfaa" }, (sfaa) => <AdminAssistantSfaaChart chart={sfaa} />)
			.with({ source: "image" }, (image) =>
				// apps/api stores the image before the turn finishes, so fetch only once it has.
				streaming || conversationId === undefined ? (
					<AdminAssistantChartNotice>Drawing chart…</AdminAssistantChartNotice>
				) : (
					<AdminAssistantImageChart chart={image} conversationId={conversationId} />
				),
			)
			.with({ source: "orgchart" }, (orgChart) =>
				// Stored before the turn finishes, like an image.
				streaming || conversationId === undefined ? (
					<AdminAssistantChartNotice>Drawing org chart…</AdminAssistantChartNotice>
				) : (
					<AdminAssistantOrgChart chart={orgChart} conversationId={conversationId} />
				),
			)
			.exhaustive();
	},
);
